import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useLazyQuery } from '@apollo/client';
import i18next from 'i18next';
import { getMuiTablePaginationI18nProps } from 'components/table/muiTablePaginationI18nProps';
import {
  REPORT_CONTACT_PERSON_COUNT_QUERY,
  REPORT_CONTACT_PERSON_LIST_QUERY,
} from '../gql';
import {
  ContactPersonReportFiltersInput,
  ContactPersonReportSortInput,
  EReportsSortDirection,
  ReportContactPersonListQuery,
} from '__generated__/graphql';
import ContactPersonReportFilters from './components/ContactPersonReportFilters';
import SortableContactPersonHeader from './components/SortableContactPersonHeader';

type ContactPersonRow = ReportContactPersonListQuery['reportContactPersonList'][number];

export default function ContactPersonReportTab() {
  const [filters, setFilters] = useState<ContactPersonReportFiltersInput>({});
  const [results, setResults] = useState<ContactPersonRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 50, totalCount: 0 });
  const [sortFields, setSortFields] = useState<ContactPersonReportSortInput[]>([
    { lastname: EReportsSortDirection.asc },
  ]);

  const [executeListQuery] = useLazyQuery(REPORT_CONTACT_PERSON_LIST_QUERY, { fetchPolicy: 'network-only' });
  const [executeCountQuery] = useLazyQuery(REPORT_CONTACT_PERSON_COUNT_QUERY, { fetchPolicy: 'network-only' });

  const handleFilterChange = (field: keyof ContactPersonReportFiltersInput, value: any) => {
    setFilters((prev) => {
      if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return { ...prev, [field]: value };
    });
  };

  const handleReset = () => {
    setFilters({});
    setResults([]);
    setPagination({ page: 0, rowsPerPage: 50, totalCount: 0 });
    setSortFields([{ lastname: EReportsSortDirection.asc }]);
  };

  const handleGenerateReport = async (page: number, rowsPerPage: number) => {
    setLoading(true);
    try {
      const [countResult, listResult] = await Promise.all([
        executeCountQuery({ variables: { filters } }),
        executeListQuery({
          variables: { filters, sort: sortFields, skip: page * rowsPerPage, take: rowsPerPage },
        }),
      ]);
      setResults(listResult.data?.reportContactPersonList || []);
      setPagination((prev) => ({ ...prev, totalCount: countResult.data?.reportContactPersonCount || 0, page }));
    } catch (error) {
      console.error('Contact person report error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSortChange = (field: keyof ContactPersonReportSortInput, direction?: EReportsSortDirection) => {
    let newSortFields: ContactPersonReportSortInput[];

    const currentSort = sortFields.find((s) => s[field]);

    if (currentSort) {
      const currentDirection = currentSort[field];
      if (currentDirection === EReportsSortDirection.asc) {
        newSortFields = [{ [field]: EReportsSortDirection.desc }];
      } else {
        newSortFields = [{ [field]: EReportsSortDirection.asc }];
      }
    } else {
      newSortFields = [{ [field]: EReportsSortDirection.asc }];
    }

    setSortFields(newSortFields);
  };

  useEffect(() => {
    if (results.length > 0) {
      handleGenerateReport(pagination.page, pagination.rowsPerPage);
    }
  }, [sortFields]);

  const handleExportCsv = async () => {
    setExportLoading(true);
    try {
      const result = await executeListQuery({
        variables: { filters, sort: sortFields, skip: 0, take: undefined },
      });
      const allRows = result.data?.reportContactPersonList || [];

      const headers = [
        String(i18next.t('contact-title')),
        String(i18next.t('contact-firstname')),
        String(i18next.t('contact-lastname')),
        String(i18next.t('contact-street')),
        String(i18next.t('contact-street-number')),
        String(i18next.t('contact-zip')),
        String(i18next.t('contact-city')),
        String(i18next.t('contact-country')),
        String(i18next.t('contact-state')),
        String(i18next.t('contact-supplement')),
        String(i18next.t('contact-email')),
        String(i18next.t('contact-phone')),
        String(i18next.t('contact-mobile')),
        String(i18next.t('filter-company')),
        String(i18next.t('contact-subjects')),
        String(i18next.t('contact-net-inquiries')),
        String(i18next.t('contact-gross-inquiries')),
        String(i18next.t('contact-reservations')),
        String(i18next.t('contact-overnights')),
        String(i18next.t('contact-participants')),
      ];
      const csvData = allRows.map((row) => [
        row.title || '',
        row.firstname,
        row.lastname,
        row.address || '',
        row.streetNumber || '',
        row.zip || '',
        row.city || '',
        row.country || '',
        row.state || '',
        row.addressSupplement || '',
        row.email,
        row.phone || '',
        row.mobile || '',
        row.companyName || '',
        row.subjects || '',
        row.netInquiries,
        row.grossRequests,
        row.reservations,
        row.overnightStays,
        row.participants,
      ]);

      const csvContent = [headers, ...csvData]
        .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\n');

      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.setAttribute('href', URL.createObjectURL(blob));
      link.setAttribute('download', `contact-persons_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <ContactPersonReportFilters filters={filters} onFilterChange={handleFilterChange} />

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 4 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleGenerateReport(0, pagination.rowsPerPage)}
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} /> : i18next.t('filter-generate-report')}
        </Button>
        <Button variant="outlined" onClick={handleReset}>
          {i18next.t('filter-reset')}
        </Button>
      </Box>

      {loading && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography>{i18next.t('report-loading')}</Typography>
        </Box>
      )}

      {!loading && results.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {pagination.totalCount} {i18next.t('report-contact-persons-found')}
          </Typography>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <SortableContactPersonHeader field="firstname" label={String(i18next.t('contact-firstname'))} sortFields={sortFields} onSort={handleSortChange} />
                  <SortableContactPersonHeader field="lastname" label={String(i18next.t('contact-lastname'))} sortFields={sortFields} onSort={handleSortChange} />
                  <TableCell>{i18next.t('contact-title')}</TableCell>
                  <TableCell>{i18next.t('contact-street')}</TableCell>
                  <TableCell>{i18next.t('contact-street-number')}</TableCell>
                  <TableCell>{i18next.t('contact-zip')}</TableCell>
                  <TableCell>{i18next.t('contact-city')}</TableCell>
                  <SortableContactPersonHeader field="country" label={String(i18next.t('contact-country'))} sortFields={sortFields} onSort={handleSortChange} />
                  <TableCell>{i18next.t('contact-state')}</TableCell>
                  <TableCell>{i18next.t('contact-supplement')}</TableCell>
                  <SortableContactPersonHeader field="email" label={String(i18next.t('contact-email'))} sortFields={sortFields} onSort={handleSortChange} />
                  <TableCell>{i18next.t('contact-phone')}</TableCell>
                  <TableCell>{i18next.t('contact-mobile')}</TableCell>
                  <SortableContactPersonHeader field="company" label={String(i18next.t('filter-company'))} sortFields={sortFields} onSort={handleSortChange} />
                  <TableCell>{i18next.t('contact-subjects')}</TableCell>
                  <SortableContactPersonHeader field="netInquiries" label={String(i18next.t('contact-net-inquiries'))} sortFields={sortFields} onSort={handleSortChange} align="right" />
                  <TableCell align="right">{i18next.t('contact-gross-inquiries')}</TableCell>
                  <TableCell align="right">{i18next.t('contact-reservations')}</TableCell>
                  <TableCell align="right">{i18next.t('contact-overnights')}</TableCell>
                  <TableCell align="right">{i18next.t('contact-participants')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.firstname}</TableCell>
                    <TableCell>{row.lastname}</TableCell>
                    <TableCell>{row.title || '–'}</TableCell>
                    <TableCell>{row.address || '–'}</TableCell>
                    <TableCell>{row.streetNumber || '–'}</TableCell>
                    <TableCell>{row.zip || '–'}</TableCell>
                    <TableCell>{row.city || '–'}</TableCell>
                    <TableCell>{row.country || '–'}</TableCell>
                    <TableCell>{row.state || '–'}</TableCell>
                    <TableCell>{row.addressSupplement || '–'}</TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>{row.phone || '–'}</TableCell>
                    <TableCell>{row.mobile || '–'}</TableCell>
                    <TableCell>{row.companyName || '–'}</TableCell>
                    <TableCell>{row.subjects || '–'}</TableCell>
                    <TableCell align="right">{row.netInquiries}</TableCell>
                    <TableCell align="right">{row.grossRequests}</TableCell>
                    <TableCell align="right">{row.reservations}</TableCell>
                    <TableCell align="right">{row.overnightStays}</TableCell>
                    <TableCell align="right">{row.participants}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={pagination.totalCount}
            page={pagination.page}
            onPageChange={(_, newPage) => {
              setPagination((prev) => ({ ...prev, page: newPage }));
              handleGenerateReport(newPage, pagination.rowsPerPage);
            }}
            rowsPerPage={pagination.rowsPerPage}
            onRowsPerPageChange={(e) => {
              const newRows = parseInt(e.target.value, 10);
              setPagination((prev) => ({ ...prev, rowsPerPage: newRows, page: 0 }));
              handleGenerateReport(0, newRows);
            }}
            rowsPerPageOptions={[25, 50, 100]}
            {...getMuiTablePaginationI18nProps()}
          />

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={exportLoading ? <CircularProgress size={20} /> : <GetAppIcon />}
              onClick={handleExportCsv}
              disabled={exportLoading}
            >
              {i18next.t('download-csv')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
